import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { MenuDomainRedirect } from '../components/MenuDomainRedirect';
import { Suspense } from 'react';
import { lazyWithReload } from '../utils/lazyWithReload';

// Lazy loaded pages
const Login = lazyWithReload(() => import('../pages/Login'));
const Register = lazyWithReload(() => import('../pages/Register'));
const ForgotPassword = lazyWithReload(() => import('../pages/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('../pages/ResetPassword'));
const PublicMenu = lazyWithReload(() => import('../pages/PublicMenu'));
const Impressum = lazyWithReload(() => import('../pages/legal/Impressum'));
const Datenschutz = lazyWithReload(() => import('../pages/legal/Datenschutz'));
const Kontakt = lazyWithReload(() => import('../pages/legal/Kontakt'));
const PublicEventList = lazyWithReload(() => import('../pages/PublicEventList'));
const PublicEventDetail = lazyWithReload(() => import('../pages/PublicEventDetail'));
const PublicBookingView = lazyWithReload(() => import('../pages/PublicBookingView'));
const OrderHistory = lazyWithReload(() => import('../pages/OrderHistory'));
const Dashboard = lazyWithReload(() => import('../pages/Dashboard'));
const Distillery = lazyWithReload(() => import('../pages/Distillery'));
const Recipes = lazyWithReload(() => import('../pages/Recipes'));
const Batches = lazyWithReload(() => import('../pages/Batches'));
const Insights = lazyWithReload(() => import('../pages/Insights'));
const Events = lazyWithReload(() => import('../pages/Events'));
const EventDetail = lazyWithReload(() => import('../pages/EventDetail'));
const EventCreate = lazyWithReload(() => import('../pages/EventCreate'));
const Venues = lazyWithReload(() => import('../pages/Venues'));
const VenueDetail = lazyWithReload(() => import('../pages/VenueDetail'));
const MenuEditor = lazyWithReload(() => import('../pages/MenuEditor'));
const Spirits = lazyWithReload(() => import('../pages/Spirits'));
const Trends = lazyWithReload(() => import('../pages/Trends'));
const ReleaseNotes = lazyWithReload(() => import('../pages/ReleaseNotes'));
const APIKeys = lazyWithReload(() => import('../pages/APIKeys'));
const Webhooks = lazyWithReload(() => import('../pages/Webhooks'));
const LiveOrders = lazyWithReload(() => import('../pages/LiveOrders'));
const Analytics = lazyWithReload(() => import('../pages/Analytics'));
const Settings = lazyWithReload(() => import('../pages/Settings'));
const TastingTemplates = lazyWithReload(() => import('../pages/TastingTemplates'));
const TastingTemplateEditor = lazyWithReload(() => import('../pages/TastingTemplateEditor'));
const TastingEventLive = lazyWithReload(() => import('../pages/TastingEventLive'));
const VenuePublicEvents = lazyWithReload(() => import('../pages/VenuePublicEvents'));
const VenueInventoryLight = lazyWithReload(() => import('../pages/VenueInventoryLight'));
const Monitoring = lazyWithReload(() => import('../pages/Monitoring'));
const PourCost = lazyWithReload(() => import('../pages/PourCost'));
const HappyHour = lazyWithReload(() => import('../pages/HappyHour'));
const VenueGuestList = lazyWithReload(() => import('../pages/VenueGuestList'));
const VenueMarketing = lazyWithReload(() => import('../pages/VenueMarketing'));
const VenueKDS = lazyWithReload(() => import('../pages/VenueKDS'));
const VenueTables = lazyWithReload(() => import('../pages/VenueTables'));
const VenueEmailBranding = lazyWithReload(() => import('../pages/VenueEmailBranding'));
const VenueTabs = lazyWithReload(() => import('../pages/VenueTabs'));
const WaiterPOS = lazyWithReload(() => import('../pages/WaiterPOS'));
const WaiterView = lazyWithReload(() => import('../pages/WaiterView'));
const TeamManagement = lazyWithReload(() => import('../pages/TeamManagement'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-dark-950">
    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <LazyPage>
        <Login />
      </LazyPage>
    ),
  },
  {
    path: '/register',
    element: (
      <LazyPage>
        <Register />
      </LazyPage>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <LazyPage>
        <ForgotPassword />
      </LazyPage>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <LazyPage>
        <ResetPassword />
      </LazyPage>
    ),
  },
  // Rechtstexte: auf beiden Domains erreichbar, ohne Anmeldung
  {
    path: '/impressum',
    element: (
      <LazyPage>
        <Impressum />
      </LazyPage>
    ),
  },
  {
    path: '/datenschutz',
    element: (
      <LazyPage>
        <Datenschutz />
      </LazyPage>
    ),
  },
  {
    path: '/kontakt',
    element: (
      <LazyPage>
        <Kontakt />
      </LazyPage>
    ),
  },
  {
    path: '/menu/:slug',
    element: (
      <LazyPage>
        <PublicMenu />
      </LazyPage>
    ),
  },
  {
    path: '/menu/:venueSlug/events',
    element: (
      <LazyPage>
        <PublicEventList />
      </LazyPage>
    ),
  },
  {
    path: '/menu/:venueSlug/events/:eventSlug',
    element: (
      <LazyPage>
        <PublicEventDetail />
      </LazyPage>
    ),
  },
  {
    path: '/bookings/:token',
    element: (
      <LazyPage>
        <PublicBookingView />
      </LazyPage>
    ),
  },
  {
    path: '/orders',
    element: (
      <LazyPage>
        <OrderHistory />
      </LazyPage>
    ),
  },
  {
    path: '/venues/:venueId/kds',
    element: (
      <ProtectedRoute>
        <LazyPage>
          <VenueKDS />
        </LazyPage>
      </ProtectedRoute>
    ),
  },
  {
    path: '/venues/:venueId/pos',
    element: (
      <ProtectedRoute>
        <LazyPage>
          <WaiterPOS />
        </LazyPage>
      </ProtectedRoute>
    ),
  },
  {
    path: '/venues/:venueId/waiter',
    element: (
      <ProtectedRoute>
        <LazyPage>
          <WaiterView />
        </LazyPage>
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <LazyPage>
            <Dashboard />
          </LazyPage>
        ),
      },
      {
        path: 'distillery',
        element: (
          <LazyPage>
            <Distillery />
          </LazyPage>
        ),
      },
      {
        path: 'brewmaster',
        children: [
          {
            index: true,
            element: <Navigate to="/brewmaster/recipes" replace />,
          },
          {
            path: 'recipes',
            element: (
              <LazyPage>
                <Recipes />
              </LazyPage>
            ),
          },
          {
            path: 'batches',
            element: (
              <LazyPage>
                <Batches />
              </LazyPage>
            ),
          },
          {
            path: 'insights',
            element: (
              <LazyPage>
                <Insights />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'events',
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <Events />
              </LazyPage>
            ),
          },
          {
            path: 'new',
            element: (
              <LazyPage>
                <EventCreate />
              </LazyPage>
            ),
          },
          {
            path: ':id',
            element: (
              <LazyPage>
                <EventDetail />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'venues',
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <Venues />
              </LazyPage>
            ),
          },
          {
            path: ':id',
            element: (
              <LazyPage>
                <VenueDetail />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/menus/:menuId',
            element: (
              <LazyPage>
                <MenuEditor />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tasting-templates',
            element: (
              <LazyPage>
                <TastingTemplates />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tasting-templates/new',
            element: (
              <LazyPage>
                <TastingTemplateEditor />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tasting-templates/:templateId',
            element: (
              <LazyPage>
                <TastingTemplateEditor />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/public-events',
            element: (
              <LazyPage>
                <VenuePublicEvents />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/guests',
            element: (
              <LazyPage>
                <VenueGuestList />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/marketing',
            element: (
              <LazyPage>
                <VenueMarketing />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/inventory-light',
            element: (
              <LazyPage>
                <VenueInventoryLight />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tasting-events/:eventId/live',
            element: (
              <LazyPage>
                <TastingEventLive />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tables',
            element: (
              <LazyPage>
                <VenueTables />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/tabs',
            element: (
              <LazyPage>
                <VenueTabs />
              </LazyPage>
            ),
          },
          {
            path: ':venueId/email-branding',
            element: (
              <LazyPage>
                <VenueEmailBranding />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'live-orders',
        element: (
          <LazyPage>
            <LiveOrders />
          </LazyPage>
        ),
      },
      {
        path: 'analytics',
        element: (
          <LazyPage>
            <Analytics />
          </LazyPage>
        ),
      },
      {
        path: 'pour-cost',
        element: (
          <LazyPage>
            <PourCost />
          </LazyPage>
        ),
      },
      {
        path: 'happy-hour',
        element: (
          <LazyPage>
            <HappyHour />
          </LazyPage>
        ),
      },
      {
        path: 'spirits',
        element: (
          <LazyPage>
            <Spirits />
          </LazyPage>
        ),
      },
      {
        path: 'trends',
        element: (
          <LazyPage>
            <Trends />
          </LazyPage>
        ),
      },
      {
        path: 'release-notes',
        element: (
          <LazyPage>
            <ReleaseNotes />
          </LazyPage>
        ),
      },
      {
        path: 'api-keys',
        element: (
          <LazyPage>
            <APIKeys />
          </LazyPage>
        ),
      },
      {
        path: 'webhooks',
        element: (
          <LazyPage>
            <Webhooks />
          </LazyPage>
        ),
      },
      {
        path: 'team',
        element: (
          <LazyPage>
            <TeamManagement />
          </LazyPage>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyPage>
            <Settings />
          </LazyPage>
        ),
      },
      {
        path: 'monitoring',
        element: (
          <LazyPage>
            <Monitoring />
          </LazyPage>
        ),
      },
    ],
  },
  {
    // Catch-all: handles menu domain redirects (siply.pro/slug -> /menu/slug)
    // and falls back to dashboard for authenticated routes
    path: '*',
    element: <MenuDomainRedirect />,
  },
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
});
